import { useState } from "react";
import { Link } from "react-router-dom";
import { MailCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSecurityMeta } from "@/components/useSecurityMeta";

const inputClass =
  "w-full rounded-xl border border-choco-line bg-choco px-4 py-2.5 text-sm text-cream-text placeholder:text-cream-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus";

export default function AdminForgotPassword() {
  useSecurityMeta();
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/admin/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-choco px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-sm rounded-3xl border border-choco-line bg-choco-light p-8">
        <img src="/assets/logo.png" alt="Bakes by CR" className="h-16 w-auto" />
        <h1 className="mt-4 font-serif text-2xl font-bold text-cream-text">Reset your password</h1>

        {done ? (
          <div className="mt-4" data-testid="forgot-password-success">
            <MailCheck className="h-8 w-8 text-gold" aria-hidden="true" />
            <p className="mt-3 text-sm leading-relaxed text-cream-text" role="status">
              If an active administrator account exists for that email address, we've sent instructions to reset the password.
            </p>
            <p className="mt-3 text-xs leading-relaxed text-cream-muted">
              The link expires in 60 minutes. While customer email is still in test mode, the message is held in the
              admin email outbox for preview rather than delivered — see Help &amp; Setup.
            </p>
            <Link to="/admin/login" className="mt-6 block text-center text-sm font-bold text-gold hover:text-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus rounded-full" data-testid="forgot-back-to-login">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} noValidate data-testid="forgot-password-form">
            <p className="mt-1 text-sm text-cream-muted">Enter your admin email address and we'll send you a reset link.</p>
            <div className="mt-6">
              <label htmlFor="forgot-email" className="mb-1.5 block text-sm font-semibold text-cream-text">Email</label>
              <input id="forgot-email" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} data-testid="forgot-email-input" />
            </div>
            {error && (
              <p className="mt-4 text-sm font-semibold text-red-400" role="alert" data-testid="forgot-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={busy || !email.trim()}
              className="mt-6 w-full rounded-full bg-gold px-6 py-3 text-sm font-bold text-choco transition-colors hover:bg-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus active:scale-[0.98] disabled:opacity-60"
              data-testid="forgot-submit-button"
            >
              {busy ? "Sending…" : "Send reset link"}
            </button>
            <Link to="/admin/login" className="mt-4 block text-center text-xs font-semibold text-cream-muted hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus rounded-full" data-testid="forgot-cancel-link">
              Back to sign in
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
